import { Component, OnInit, OnDestroy, Input } from '@angular/core';

import { forkJoin, of, Subscription, Observable } from 'rxjs';
import { mergeMap, map } from 'rxjs/operators';
import moment from 'moment-timezone';

import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';
import { MediaSubscriptionStatusEnum } from '../../enums/media-subscription-status.enum';
import { LineChartModel } from '../../../../Common/Charts/models/lineChart/line-chart.model';
import { MediaSubscription } from '../../../Shared/models/media-subscription.model';

import { LineChartOptionsModel } from '../../../../Common/Charts/models/lineChart/line-chart-options.model';
import { QuotaInformation } from '../../../../api/mediaSubscriptions/models/quota-information.api.model';
import { SubscriptionContentTimeslot } from '../../../../api/mediaSubscriptions/models/subscription-content-timeslot.api.model';
import { DataPointModel } from '../../../../Common/Charts/models/data-point.model';

import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { LineSeriesModel } from '../../../../Common/Charts/models/lineChart/line-series.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { AgentService } from '../../../../Social/Intelligence/services/agent.service';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { MonitoringQuotaTypesEnum } from '../../../../api/mentions/enum/monitoring-quota-types.enum';

@Component({
    selector: 'agpr-quota-usage-over-time-chart',
    templateUrl: './quota-usage-over-time-chart.component.html',
    styleUrls: ['./quota-usage-over-time-chart.component.scss'],
    standalone: false
})
export class QuotaUsageOverTimeChartComponent implements OnInit, OnDestroy {
    @Input() isForAgent: boolean = false;

    subscriptions: Subscription[] = [];
    chart: LineChartModel;
    chartOptions: LineChartOptionsModel;

    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;

    requests: Subscription;

    focusedSubscription: MediaSubscription;
    quotaInfo: QuotaInformation;

    loading: boolean = false;

    // Need a boolean to check for this because the focused subscription may be null (on the dashboard)
    private gotFocusedSubscription: boolean = false;

    constructor(
        private mediaSubscriptionService: MediaSubscriptionService,
        private agentService: AgentService,
        private localizationService: LocalizationService
    ) {}

    ngOnInit() {
        this.loading = true;
        this.chartOptions = {
            precision: 0,
            xValueLabel: this.localizationService.transform('MediaSubscriptions.dashboard.quotaOverTime.timeAxis'),
            yValueLabel: this.localizationService.transform('MediaSubscriptions.dashboard.quotaOverTime.quotaAxis'),
            numberFormat: '#a'
        };
        this.subscriptions.push(
            this.mediaSubscriptionService.currentResource$.subscribe((subscription) => {
                this.gotFocusedSubscription = true;
                this.focusedSubscription = subscription;
                this.generateChart();
            }),
            this.mediaSubscriptionService.getQuotaInfo(MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION).subscribe((quotaInfo) => {
                if (!quotaInfo) {
                    return;
                }

                this.quotaInfo = quotaInfo;
                this.generateChart();
            })
        );
    }

    ngOnDestroy(): void {
        this.destroyOldChart();
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
    }

    destroyOldChart(): void {
        this.chart = null;

        if (this.requests) {
            this.requests.unsubscribe();
            this.requests = null;
        }
    }

    generateChart(): void {
        this.destroyOldChart();

        if (!(this.quotaInfo && this.gotFocusedSubscription)) {
            return;
        }

        this.loading = true;

        const reqs: Observable<any>[] = [];

        reqs.push(this.agentService.getUsageOverTimeSummary());
        reqs.push(this.mediaSubscriptionService.getUsageOverTimeSummary());

        if (this.focusedSubscription) {
            const individual: Observable<any>[] = [];
            const sub = this.focusedSubscription;
            individual.push(
                this.mediaSubscriptionService.getSubscriptionUsageOverTime(sub.id).pipe(
                    map((data) => {
                        return {sub, data};
                    }))
            );
            reqs.push(forkJoin(individual));
        } else {
            // Get the top 10 subscriptions (by usage)
            let topSubscriptionsRequest: Observable<{
                subscription: MediaSubscription;
                count: number;
            }[]>;
            if (this.isForAgent) {
                topSubscriptionsRequest = this.agentService.getTopSubscriptions(10);
            } else {
                topSubscriptionsRequest = this.mediaSubscriptionService.getTopSubscriptions(10);
            }
            const subsContentRequest = topSubscriptionsRequest.pipe(
                mergeMap((topSubsWithCount) => {
                    if (!topSubsWithCount || topSubsWithCount.length < 1) {
                        // has to be an array of arrays to accomodate flatMap
                        return [[]];
                    }

                    // Sort them, then get the subscriptions
                    const topSubs = topSubsWithCount.sort((a, b) => a.count - b.count).map(subWithCount => subWithCount.subscription);

                    if (topSubs.length === 10) {
                        topSubs.splice(0, 1);
                    }

                    // Get the coverage over time for each subscription
                    return forkJoin(
                        topSubs.map(sub => {
                            return this.mediaSubscriptionService.getSubscriptionUsageOverTime(sub.id).pipe(
                                map((data) => ({sub, data}))
                            );
                        })
                    );
                })
            );

            reqs.push(subsContentRequest);
        }

        this.requests = forkJoin(reqs).subscribe((responses) => {
            this.requests.unsubscribe();
            this.requests = null;

            const quotaInfo = this.quotaInfo;
            const globalSocialUsage = <SubscriptionContentTimeslot[]>responses[0];
            const globalNonSocialUsage = <SubscriptionContentTimeslot[]>responses[1];
            const subscriptionUsage = <{sub: MediaSubscription; data: SubscriptionContentTimeslot[]}[]>responses[2];
            const quotaEndDay = moment(quotaInfo.nextResetDate).utc().subtract(1, 'minute').date();

            this.chartOptions.minYValue = 0;
            this.chartOptions.maxYValue = (quotaInfo.maxCount + quotaInfo.additionalCount) * 1.05;
            this.chartOptions.minXValue = 0;
            this.chartOptions.maxXValue = quotaEndDay;

            this.chartOptions.horizontalLines = [
                {
                    position: quotaInfo.maxCount + quotaInfo.additionalCount,
                    color: chartColors.agprMediumBlue,
                    strokeWidth: 2,
                    tooltip: this.localizationService.transform('MediaSubscriptions.dashboard.quotaOverTime.quotaLine')
                }
            ];

            // combine social and non-social count for the 'All' line
            let globalUsage: SubscriptionContentTimeslot[] = [];
            if (globalSocialUsage?.length > 0 && globalNonSocialUsage?.length > 0) {
                for (let i = 0; i < globalSocialUsage.length; i++) {
                    const timeSlot: SubscriptionContentTimeslot = {
                        from: null,
                        to: null,
                        count: 0
                    };
                    timeSlot.from = globalSocialUsage[i].from;
                    timeSlot.to = globalSocialUsage[i].to;
                    timeSlot.count = globalSocialUsage[i].count + globalNonSocialUsage[i].count;
                    globalUsage.push(timeSlot);
                }
            } else if (!globalSocialUsage || globalSocialUsage.length === 0) {
                globalUsage = globalNonSocialUsage || [];
            } else if (!globalNonSocialUsage || globalNonSocialUsage.length === 0) {
                globalUsage = globalSocialUsage || [];
            } else {
                globalUsage = [];
            }
            const globalDataPoints = this.convertTimeslotsToDataPoints(globalUsage);
            this.chartOptions.verticalLines = [
                {
                    position: globalDataPoints[globalDataPoints.length - 1].xValue,
                    color: chartColors.agprDarkPurple,
                    strokeWidth: 2,
                    tooltip: this.localizationService.transform('MediaSubscriptions.dashboard.quotaOverTime.todayLine')
                }
            ];

            const chartSeries: LineSeriesModel[] = [
                // Add the "All Subscriptions" series
                {
                    label: this.localizationService.transform('MediaSubscriptions.dashboard.quotaOverTime.allSubscriptions'),
                    dataProvider: of(globalDataPoints),
                    color: this.defaultChartColors.generalColors.color1,
                    useProjection: true,
                    projectionStrokeSize: 2,
                    projectionStrokeDashArray: '8,3'
                }
            ];

            if (subscriptionUsage.length > 0) {
                // Add individual series lines
                chartSeries.push(...subscriptionUsage
                    .filter(subscription => !!subscription.sub?.isAgent === this.isForAgent)
                    .map(({sub, data}) => {
                        const project = sub.status === MediaSubscriptionStatusEnum.ACTIVE;

                        return {
                            label: sub.name,
                            dataProvider: of(this.convertTimeslotsToDataPoints(data)),
                            useProjection: project,
                            projectionStrokeSize: 1,
                            projectionStrokeDashArray: '8,3'
                        };
                    })
                );
            }


            this.chart = new LineChartModel(chartSeries, this.chartOptions);
            this.loading = false;
        });
    }

    /**
     * Converts a series of timeslots for subscription content into chartable data points
     */
    convertTimeslotsToDataPoints(timeslots: SubscriptionContentTimeslot[]): DataPointModel[] {
        const data = [{
            label: '0',
            xValue: 0,
            yValue: 0
        }];
        let total = 0;
        for (let i = 0; i < timeslots.length; i++) {
            const slot = timeslots[i];
            const day = moment(slot.from).utc().date();
            total += slot.count;
            data.push({label: '' + day, xValue: day, yValue: total});
        }

        return data;
    }
}
